import { SlideOut } from '@interstate/components/SlideOut';
import * as bridgeUserSelectors from '@makemydeal/dr-dash-store/selectors/bridgeUserSelectors';
import * as offerSelectors from '@makemydeal/dr-dash-store/selectors/offerSelectors';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import { createRemoteModule } from '@makemydeal/dr-shared-components';
import { offerSessionUtils } from '@makemydeal/dr-shared-ui-utils';
import { dealerSelectors } from '@makemydeal/dr-shared-store';
import type { ComponentType, FC } from 'react';
import { Suspense, useMemo } from 'react';
import { useSelector } from 'react-redux';
import type { CustomerDetailsProps, CustomerSidebarProps } from './CustomerSidebarProps';
import { CustomerSideBarTabEnum } from './CustomerSidebarProps';
import { OfferSaveBusyIndicator } from './OfferSaveBusyIndicator';
import { RemoteModuleErrorBoundary } from './RemoteModuleErrorBoundary';
import { useCustomerCardJwtToken } from './useCustomerCardJwtToken';
import { CUSTOMER_REQUIRED_FIELDS_FOR_PBC } from './types/CustomerSideBarRequiredFields';
import { LoadingIndicator } from '../common/LoadingIndicator';
import { createDealLockStyles } from './constants';

const CustomerSidebarModule: ComponentType<CustomerSidebarProps> = createRemoteModule<CustomerSidebarProps>(
    'customer',
    './CustomerSidebarModule'
);

export const CustomerDetails: FC<CustomerDetailsProps> = ({
    commonConsumerId,
    show,
    setShow,
    onCustomerChanged,
    messageContentSelector,
    waitForOfferSave,
    tab,
    enableReadOnlyEditFields,
    occDefaultTab
}) => {
    const defaultMessageContent = useSelector(messageContentSelector ?? (() => ''));
    const username = useSelector(bridgeUserSelectors.getBridgeUsername);
    const fullName = useSelector(bridgeUserSelectors.getFullName);
    const nameShown = fullName.length ? fullName : username;

    const environment = offerSessionUtils.getEnvironmentType();
    const getJwtToken = useCustomerCardJwtToken('CustomerSidebarModule');
    const handleCloseSlider = () => setShow(false);

    const isBusinessShopper = useSelector(offerSelectors.getIsBusinessShopper);
    const shopperInfo = useSelector(offerReduxSelectors.getShopperInfo);
    const coBuyerInfo = useSelector(offerReduxSelectors.getCoBuyerInfo);
    const formatConsumerName = (consumer?: { firstName?: string; middleName?: string; lastName?: string }) =>
        consumer?.middleName
            ? `${consumer.firstName} ${consumer.middleName} ${consumer.lastName}`.trim()
            : `${consumer?.firstName ?? ''} ${consumer?.lastName ?? ''}`.trim();
    /* istanbul ignore next */
    const fullBuyerName = formatConsumerName(shopperInfo);
    const fullCobuyerName = formatConsumerName(coBuyerInfo);
    const isCoBuyer = commonConsumerId !== shopperInfo.commonConsumerId;
    const customerHeaderName = isCoBuyer ? fullCobuyerName : fullBuyerName;
    const enableInbox = isCoBuyer ? true : !isBusinessShopper;
    const isMvLiteEnabled = useSelector(dealerSelectors.isMvLiteEnabled);
    // Sourced for New Relic enrichment so MV:CCC:ModuleRenderError events on
    // the "MV — Common Consumer Card Integration Health" dashboard can be
    // sliced / drilled-into per dealer (string-cast for stable NRQL FACET grouping).
    const dealerId = useSelector(dealerSelectors.getDealerId);
    const dealerIdAttr = dealerId != null ? String(dealerId) : '';

    const sidebarProps: CustomerSidebarProps = useMemo(
        () => ({
            commonConsumerID: commonConsumerId,
            environment,
            getAuthOverride: getJwtToken,
            doneCallback: handleCloseSlider,
            occConfig: enableInbox
                ? ({ username: nameShown, messageContent: defaultMessageContent, defaultTab: occDefaultTab || 'text' } as any)
                : undefined,
            editCapability: 'hidden',
            onCustomerEdited: onCustomerChanged,
            defaultTab: tab,
            enableReadOnlyEditFields,
            requiredFields: CUSTOMER_REQUIRED_FIELDS_FOR_PBC,
            ...(isMvLiteEnabled && { visibleTabs: [CustomerSideBarTabEnum.Information, CustomerSideBarTabEnum.Inbox] })
        }),
        [
            commonConsumerId,
            environment,
            getJwtToken,
            handleCloseSlider,
            enableInbox,
            nameShown,
            defaultMessageContent,
            occDefaultTab,
            onCustomerChanged,
            tab,
            enableReadOnlyEditFields,
            isMvLiteEnabled
        ]
    );

    return (
        <SlideOut
            show={show}
            header={customerHeaderName}
            data-testid="customer-details-drawer"
            position="right"
            panelWidth="auto"
            sx={{
                '.MuiPaper-root': {
                    width: { xs: 1, md: 584 }
                },
                '.interstate-close-sliding-drawer-button ': {
                    margin: '1rem'
                },
                '.interstate-sliding-drawer-dialog-title-container': {
                    'padding-top': 0,
                    'padding-bottom': 0
                },
                '.MuiDialogContent-root': {
                    'padding-bottom': '1rem'
                },
                'div>.MuiDialogContent-root>div:first-child': {
                    // more specific; this is a hack to make the customer card fill the drawer
                    height: '100%'
                },
                '#sliding-drawer-footer-actions': {
                    display: 'none'
                },
                '#customer-form-work-phone-extension-root>label:first-child': {
                    padding: 0
                },
                '.sidebar .backButton': {
                    display: 'none !important'
                },
                // Disable VinSolutions and Change History buttons when deal is locked
                ...createDealLockStyles(enableReadOnlyEditFields)
            }}
            onHide={handleCloseSlider}
        >
            {waitForOfferSave ? (
                <OfferSaveBusyIndicator>
                    <RemoteModuleErrorBoundary
                        moduleName="CustomerSidebarModule"
                        commonConsumerId={commonConsumerId}
                        dealerId={dealerIdAttr}
                    >
                        <Suspense fallback={<LoadingIndicator message="Loading..." />}>
                            <CustomerSidebarModule {...sidebarProps} />
                        </Suspense>
                    </RemoteModuleErrorBoundary>
                </OfferSaveBusyIndicator>
            ) : (
                <RemoteModuleErrorBoundary
                    moduleName="CustomerSidebarModule"
                    commonConsumerId={commonConsumerId}
                    dealerId={dealerIdAttr}
                >
                    <Suspense fallback={<LoadingIndicator message="Loading..." />}>
                        <CustomerSidebarModule {...sidebarProps} />
                    </Suspense>
                </RemoteModuleErrorBoundary>
            )}
        </SlideOut>
    );
};
